// TODO: Move to talentProfile folder since its both used by skills and work titles

import { Skill, WorkTitle } from "../orders/orderAPI";

/**
 * A skill with a localized name
 *
 * @see Skill
 */
export interface LocalizedSkill extends Skill {
  name: string;
}

/**
 * A work title with a localized name
 *
 * @see WorkTitle
 */
export interface LocalizedWorkTitle extends WorkTitle {
  name: string;
}

/**
 * A map where the key is a field of work
 * and the value is a list of work titles
 * which have the same field of work
 *
 * @see LocalizedWorkTitle
 */
export type WorkTitleGroups = {
  [fieldOfWork: string]: LocalizedWorkTitle[];
};

/**
 * A map where the key is a field of work
 * and the value is a list of skills
 * which have the same field of work
 *
 * @see LocalizedSkill
 */
export type SkillGroups = {
  [fieldOfWork: string]: LocalizedSkill[];
};

type LocalizationMap = { [id: string]: string };

/**
 * Maps skills under their field of work.
 *
 * @param skills - skills to be grouped
 * @param localizationMaps - localization maps for field of work and skill
 *
 * @returns SkillGroups - map of fields of work and their skills
 *
 * @see SkillGroups
 * @see Skill
 */
export const groupSkills = (
  skills: Skill[],
  localizationMaps: {
    fieldOfWorkId: LocalizationMap;
    skillId: LocalizationMap;
  },
): SkillGroups => {
  return skills.reduce<SkillGroups>((skillGroup, skill) => {
    const fieldOfWork = localizationMaps.fieldOfWorkId[skill.fieldOfWorkId];

    if (!fieldOfWork) return skillGroup;

    if (!skillGroup[fieldOfWork]) {
      skillGroup[fieldOfWork] = [];
    }

    skillGroup[fieldOfWork].push({
      id: skill.id,
      fieldOfWorkId: skill.fieldOfWorkId,
      name: localizationMaps.skillId[skill.id],
    });

    return skillGroup;
  }, {});
};

/**
 * Maps work titles under their field of work.
 *
 * @param workTitles
 * @param localizationMaps
 *
 * @returns WorkTitleGroups - A map where the key is the field of work and the value is an array of work titles
 *
 * @see WorkTitleGroups
 * @see WorkTitle
 */
export const groupWorkTitles = (
  workTitles: WorkTitle[],
  localizationMaps: {
    workTitle: LocalizationMap;
    fieldOfWork: LocalizationMap;
  },
): WorkTitleGroups => {
  return workTitles.reduce<WorkTitleGroups>((workTitleGroup, workTitle) => {
    const fieldOfWorkId = workTitle.fieldOfWorkId;

    // FIXME: Drops worktitle - Refactor when WorkTitle's fieldOfWorkId becomes required
    //  Note: A WorkTitle always has a fieldOfWorkId when returned from back-end
    if (!fieldOfWorkId) return workTitleGroup;

    const localizedFieldOfWork = localizationMaps.fieldOfWork[fieldOfWorkId];

    if (!workTitleGroup[localizedFieldOfWork]) {
      workTitleGroup[localizedFieldOfWork] = [];
    }

    workTitleGroup[localizedFieldOfWork].push({
      id: workTitle.id,
      fieldOfWorkId: workTitle.fieldOfWorkId,
      name: localizationMaps.workTitle[workTitle.id],
    });

    return workTitleGroup;
  }, {});
};
